import { useAuth } from "@stardeck-customer-apps/project-auth";
import { Code2, FileText, FolderOpen, Image, LogOut, Newspaper, Tag } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

interface NavItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  count?: number;
}

interface AdminLayoutProps {
  children: React.ReactNode;
  postCounts?: {
    blog: number;
    caseStudy: number;
  };
}

export function AdminLayout({ children, postCounts }: AdminLayoutProps) {
  const location = useLocation();
  const { signOut, user } = useAuth();

  const navItems: NavItem[] = [
    {
      name: "Blog Posts",
      href: "/posts/blog",
      icon: <Newspaper className="h-4 w-4" />,
      count: postCounts?.blog,
    },
    {
      name: "Case Studies",
      href: "/posts/case-study",
      icon: <FolderOpen className="h-4 w-4" />,
      count: postCounts?.caseStudy,
    },
    {
      name: "Media Library",
      href: "/media",
      icon: <Image className="h-4 w-4" />,
    },
    {
      name: "Categories",
      href: "/categories",
      icon: <Tag className="h-4 w-4" />,
    },
    {
      name: "API Guide",
      href: "/api-guide",
      icon: <Code2 className="h-4 w-4" />,
    },
  ];

  const isActive = (href: string) => {
    if (href === "/posts/blog") {
      return location.pathname === href || location.pathname.startsWith("/posts/blog/");
    }
    if (href === "/posts/case-study") {
      return location.pathname === href || location.pathname.startsWith("/posts/case-study/");
    }
    return location.pathname === href || location.pathname.startsWith(href);
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-60 border-r border-border bg-background flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-border">
          <div className="flex h-7 w-7 items-center justify-center rounded bg-primary">
            <FileText className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-foreground">Stardeck CMS</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive(item.href)
                  ? "bg-accent text-foreground font-medium"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              }`}
            >
              {item.icon}
              <span>{item.name}</span>
              {item.count !== undefined && (
                <span className="ml-auto text-xs text-muted-foreground">{item.count}</span>
              )}
            </Link>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-blue-400 text-white text-xs font-medium">
              {user?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2) || "AD"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {user?.name || "Admin"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email || "admin@stardeck.io"}
              </p>
            </div>
            <button
              onClick={() => signOut()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
